import React, { useState, useEffect } from "react";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import { toast } from "sonner";
import useUserData from "@/hooks/useUserData";
import { getCookie } from "@/lib/cookies";
import { API_URL } from "@/env";
import { showCustomToast } from "../CustomToast";
import CreateAccount from "../CreateAccount";
import { AnimatePresence } from "motion/react";

const JoinWorkspaceFooter = () => {
  const [showRegister, setShowRegister] = useState(false);
  const { currentWorkspace } = useWorkspaceStore();
  // const { user } = useAuth(); // Get current user
  const { user } = useUserData();
  const token = getCookie("token");
  const [isLoading, setIsLoading] = useState(false);
  const [requestMade, setRequestMade] = useState(false);

  useEffect(() => {
    const checkJoinRequest = async () => {
      if (!currentWorkspace?._id || !user?._id || !token) return;

      try {
        const response = await fetch(
          `${API_URL}/workspaces/${currentWorkspace._id}/check-join-request`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setRequestMade(data.hasPendingRequest);
        }
      } catch (error) {
        console.error("Error checking join request:", error);
      }
    };

    checkJoinRequest();
  }, [currentWorkspace?._id, user?._id, token]);

  const handleJoinWorkspace = async () => {
    if (!currentWorkspace?._id) {
      showCustomToast("Unable to join workspace");
      return;
    }

    if (user?._id) {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/workspaces/${currentWorkspace._id}/join-request`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`, // or however you store your token
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          // console.log({ response });
          if (data?.message === "already_member") {
            showCustomToast("Join request already sent", "exclamation");
          }
          throw new Error(data.message || "Failed to send join request");
        }

        showCustomToast("Join request sent successfully", "check");
        setRequestMade(true);
        // Optionally, you could redirect or update UI state here
      } catch (error) {
        console.error("Error sending join request:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to send join request. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      setShowRegister(true);
    }
  };

  return (
    <>
      {currentWorkspace && currentWorkspace?.reports?.length > 0 && (
        <div className="pointer-events-none h-[500px] sticky bottom-0 left-0 w-full z-[100] relative">
          {/* Four stacked background layers */}
          <div className="absolute inset-0 grey-linear-gradient"></div>
          <div className="absolute inset-0 grey-linear-gradient"></div>
          <div className="absolute inset-0 grey-linear-gradient"></div>
          <div className="absolute inset-0 grey-linear-gradient"></div>
        </div>
      )}

      <div
        className={`${
          currentWorkspace && currentWorkspace?.reports?.length > 0
            ? "sticky mt-[-244px] bottom-[64px] z-[1000]"
            : "absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
        } text-center w-[320px] mx-auto flex flex-col items-center`}
      >
        <div className="relative text-[32px] leading-[48px] font-semibold">
          <div
            style={{ color: currentWorkspace?.color || "#FF0088" }}
            className="absolute uppercase top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-white z-[10]"
          >
            {currentWorkspace?.name[0]}
          </div>
          <div
            style={{ background: currentWorkspace?.color || "#FF0088" }}
            className="opacity-[0.16] uppercase w-[64px] h-[64px] flex items-center justify-center rounded-[21.33px]"
          />
        </div>
        <p className="text-[20px] mt-8 leading-[28px] font-medium">
          Join {currentWorkspace?.name}
        </p>
        <p className="text-[14px] leading-[20px] mt-3 text-gray-800">
          You will be able to contribute to the workspace, create and work
          together with the team
        </p>
        <button
          disabled={requestMade || isLoading}
          onClick={handleJoinWorkspace}
          // disabled={isLoading}
          className="px-4 mt-8 gap-1.5 items-center justify-center font-medium h-[36px] text-[14px] leading-[20px] bg-white hover:bg-white/[0.8] text-gray-50 transition-all rounded-[14px] disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
        >
          {isLoading
            ? "Sending request..."
            : requestMade
            ? "Access requested"
            : "Join Workspace"}
        </button>
      </div>
      <AnimatePresence>
        {showRegister && (
          <CreateAccount
            // modalTitle="Sign up to join workspace"
            workspaceName={currentWorkspace?.name}
            workspaceId={currentWorkspace?._id}
            setShowRegister={setShowRegister}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default JoinWorkspaceFooter;
